import { BACKEND_URL } from "../env";
import { AppDeletionJob, DeletionPlan } from "../types/app-deletion";

const options = { credentials: "include", cache: "no-store" } as const;

/** Reads the backend error message, so a refused delete says why. */
async function errorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { message?: string | string[] };
    return (
      [body.message].flat().filter(Boolean).join(" ") || response.statusText
    );
  } catch {
    return response.statusText;
  }
}

async function request<T>(
  path: string,
  init?: RequestInit
): Promise<{ data: T } | { error: string }> {
  try {
    const response = await fetch(`${BACKEND_URL}${path}`, {
      ...options,
      ...init,
      headers: { "Content-Type": "application/json", ...init?.headers },
    });
    if (response.status >= 400) {
      return { error: await errorMessage(response) };
    }
    return { data: (await response.json()) as T };
  } catch (error) {
    return { error: error instanceof Error ? error.message : String(error) };
  }
}

export const fetchDeletionPlan = (appId: string) =>
  request<DeletionPlan>(`/admin/apps/${appId}/deletion-plan`);

export const startAppDeletion = (
  appId: string,
  body: { fingerprint: string; confirmName: string }
) =>
  request<AppDeletionJob>(`/admin/apps/${appId}/deletions`, {
    method: "POST",
    body: JSON.stringify(body),
  });

export const fetchDeletionJob = (jobId: string) =>
  request<AppDeletionJob>(`/admin/app-deletions/${jobId}`);

export const fetchOpenDeletions = () =>
  request<AppDeletionJob[]>(`/admin/app-deletions`);

export const resumeAppDeletion = (jobId: string) =>
  request<AppDeletionJob>(`/admin/app-deletions/${jobId}/resume`, {
    method: "POST",
  });
